import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import { api } from '../../lib/api';
import { Button, Card } from '../../components/ui/Kit';
import EmptyState from '../../components/ui/EmptyState';
import Spinner from '../../components/ui/Spinner';
import Pagination from '../../components/ui/Pagination';
import { useToast } from '../../components/ui/Toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiInbox } = FiIcons;

const PAGE_SIZE = 25;

function formatDate(value) {
  if (!value) return '—';
  const d = typeof value === 'string' ? parseISO(value) : new Date(value);
  return isValid(d) ? format(d, 'MMM d, yyyy h:mm a') : '—';
}

export default function FunnelSubmissions() {
  const { id } = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [funnel, setFunnel] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [funnelData, result] = await Promise.all([
        api.funnels.getById(id),
        api.funnelSubmissions.getByFunnel(id, { page, pageSize: PAGE_SIZE }),
      ]);
      if (!funnelData) {
        addToast({ type: 'error', title: 'Funnel not found' });
        navigate('/funnels');
        return;
      }
      setFunnel(funnelData);
      setSubmissions(result.data ?? []);
      setTotalCount(result.count ?? 0);
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to load submissions', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [id, page, addToast, navigate]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/funnels/${id}`)}>
          <SafeIcon icon={FiArrowLeft} className="mr-1" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Submissions</h1>
          <p className="text-sm text-gray-500">{funnel?.name ?? '…'}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Spinner size="lg" className="text-primary" />
        </div>
      ) : submissions.length === 0 ? (
        <EmptyState
          icon={FiInbox}
          title="No submissions yet"
          description="Submissions will appear here when visitors opt in through your live funnel."
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">Entry</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">Submitted</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">Lead</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {submissions.map((sub) => (
                  <tr key={sub.id} className="hover:bg-gray-50/80">
                    <td className="px-6 py-4 font-medium text-gray-900">
                      {[sub.first_name, sub.last_name].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td className="px-6 py-4 text-gray-600">{sub.email || '—'}</td>
                    <td className="px-6 py-4 text-gray-600">{sub.phone || '—'}</td>
                    <td className="px-6 py-4 text-gray-600">Step {sub.entry_point ?? 1}</td>
                    <td className="px-6 py-4 text-gray-600">{formatDate(sub.created_at)}</td>
                    <td className="px-6 py-4">
                      {sub.lead_id ? (
                        <Link to={`/leads/${sub.lead_id}`} className="text-primary hover:underline">
                          View lead
                        </Link>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalCount > PAGE_SIZE && (
            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={totalCount}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          )}
        </Card>
      )}
    </div>
  );
}
